import { useConversation } from "@pages/Assistant/utils/useConversation";
import React, { useState, useRef, useEffect } from "react";
import { HiDotsVertical } from "react-icons/hi";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ReactDOM from "react-dom";
import { useSidebar } from "@services/SidebarContext";
import DeleteEntityModal from "@pages/Program/components/DeleteEntity";

interface Conversation {
  id: number;
  title: string;
}

interface ConversationListProps {
  conversations: Conversation[];
  activeColor?: string;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeColor,
}) => {
  const [openPopupId, setOpenPopupId] = useState<number | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});
  const { deleteConversation } = useConversation();
  const { conversationId } = useParams();
  const { refreshChats } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchConversationId = searchParams.get("conversationId");
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Conversation | null>(
    null
  );

  const handleClickOutside = (event: MouseEvent) => {
    // Only close popup if clicked outside of both the button and popup
    const isOutside = Object.entries(buttonRefs.current).every(
      ([id, button]) => {
        if (!button) return true;
        return !button.contains(event.target as Node);
      }
    );

    if (isOutside) {
      setOpenPopupId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntity) return;
    try {
      await deleteConversation(selectedEntity.id);

      if (
        conversationId === String(selectedEntity.id) ||
        searchConversationId === String(selectedEntity.id)
      ) {
        setSearchParams({});
        navigate(`/assistant?new=1`);
      }

      refreshChats();
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    } finally {
      setOpenPopupId(null);
      setShowDeleteModal(false);
      setSelectedEntity(null);
    }
  };

  const openDeleteModal = (entity: Conversation) => {
    setSelectedEntity(entity);
    setShowDeleteModal(true);
    setOpenPopupId(null);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleOpenPopup = (id: number) => {
    const button = buttonRefs.current[id];
    if (button) {
      const rect = button.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 4,
        left: rect.left - 80, // adjust this value for horizontal position
      });
    }
    setOpenPopupId(id);
  };

  return (
    <>
      <ul className="space-y-2 mt-1 font-light pl-4">
        {conversations.map((c) => (
          <li key={c.id} className="relative">
            <Link
              to={`/assistant/conversation/${c.id}`}
              className={`flex items-center p-2 space-x-3 rounded-md hover:bg-[${activeColor}] ${
                (conversationId === String(c.id) ||
                  searchConversationId === String(c.id)) &&
                `bg-[${activeColor}]`
              }`}
            >
              <span className="line-clamp-1 pr-4">{c.title}</span>
            </Link>

            <div className="absolute right-2 top-2">
              <button
                ref={(ref) => {
                  buttonRefs.current[c.id] = ref;
                }}
                onClick={() => handleOpenPopup(c.id)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full focus:outline-none"
              >
                <HiDotsVertical size={16} />
              </button>
            </div>

            {/* Render the popup into body via portal */}
            {openPopupId === c.id &&
              popupPosition &&
              ReactDOM.createPortal(
                <div
                  style={{
                    position: "absolute",
                    top: popupPosition.top,
                    left: popupPosition.left,
                    zIndex: 9999,
                  }}
                  className="w-28 bg-white border border-gray-200 rounded-md shadow-lg"
                >
                  <button
                    onClick={() => openDeleteModal(c)}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Delete
                  </button>
                </div>,
                document.body
              )}
          </li>
        ))}
      </ul>
      <DeleteEntityModal
        isOpen={showDeleteModal}
        selectedEntity={selectedEntity}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ConversationList;
